import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  getAlumnos, createAlumno, updateAlumno, bulkUploadAlumnos, 
  downloadAlumnosTemplate, getCiclos, deleteAlumno
} from '../lib/api';
import { formatNombreCompleto } from '../lib/nombre';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Plus, MagnifyingGlass, Upload, Download, Eye, PencilSimple, Trash,
  Funnel, X, Student, Warning
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';

const AlumnosPage = () => {
  const { user, hasPermission } = useAuth();
  const canDelete = hasPermission(['administrador', 'direccion']);
  const [alumnos, setAlumnos] = useState([]);
  const [ciclos, setCiclos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAlumno, setSelectedAlumno] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const [filters, setFilters] = useState({
    matricula: '',
    nombre: '',
    grado: '',
    grupo: '',
    turno: '',
    estatus: '',
    en_patre: '',
    incluir_egresados: false,
  });

  const [formData, setFormData] = useState({
    matricula: '',
    nombre: '',
    apellido_paterno: '',
    apellido_materno: '',
    grado: '',
    grupo: '',
    turno: '',
    domicilio: '',
    telefono_contacto: '',
    telefono_emergencia: '',
    curp: '',
    nombre_tutor: '',
    telefono_tutor: '',
    ciclo_escolar_id: ''
  });

  const fetchAlumnos = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      const parseBooleanFilter = (v) => {
        if (v === 'true') return true;
        if (v === 'false') return false;
        return v;
      };
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params[key] = parseBooleanFilter(value);
        }
      });
      
      const response = await getAlumnos(params);
      setAlumnos(response.data.alumnos);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Error al cargar alumnos');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchAlumnos();
  }, [fetchAlumnos]);

  useEffect(() => {
    const fetchCiclos = async () => {
      try {
        const response = await getCiclos();
        setCiclos(response.data);
        const activeCiclo = response.data.find(c => c.activo);
        if (activeCiclo) {
          setFormData(prev => ({ ...prev, ciclo_escolar_id: activeCiclo.id }));
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error fetching ciclos:', error);
        }
      }
    };
    fetchCiclos();
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      matricula: '',
      nombre: '',
      grado: '',
      grupo: '',
      turno: '',
      estatus: '',
      en_patre: ''
    });
    setPage(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode && selectedAlumno) {
        await updateAlumno(selectedAlumno.id, formData);
        toast.success('Alumno actualizado correctamente');
      } else {
        await createAlumno(formData);
        toast.success('Alumno creado correctamente');
      }
      setShowModal(false);
      resetForm();
      fetchAlumnos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar alumno');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      const response = await bulkUploadAlumnos(file);
      setUploadResult(response.data);
      toast.success(`${response.data.created} alumnos cargados correctamente`);
      fetchAlumnos();
    } catch (error) {
      toast.error('Error al cargar archivo');
    }
    e.target.value = '';
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadAlumnosTemplate();
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_alumnos.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error al descargar plantilla');
    }
  };

  const openEditModal = (alumno) => {
    setSelectedAlumno(alumno);
    setFormData({
      matricula: alumno.matricula,
      nombre: alumno.nombre,
      apellido_paterno: alumno.apellido_paterno,
      apellido_materno: alumno.apellido_materno || '',
      grado: alumno.grado,
      grupo: alumno.grupo,
      turno: alumno.turno,
      domicilio: alumno.domicilio || '',
      telefono_contacto: alumno.telefono_contacto || '',
      telefono_emergencia: alumno.telefono_emergencia || '',
      curp: alumno.curp || '',
      nombre_tutor: alumno.nombre_tutor || '',
      telefono_tutor: alumno.telefono_tutor || '',
      estatus: alumno.estatus || 'activo',
      en_patre: alumno.en_patre || false,
      motivo_patre: alumno.motivo_patre || '',
      materias_riesgo: alumno.materias_riesgo || [],
      observaciones_patre: alumno.observaciones_patre || ''
    });
    setEditMode(true);
    setShowModal(true);
  };

  const openDetailModal = (alumno) => {
    setSelectedAlumno(alumno);
    setShowDetailModal(true);
  };

  const resetForm = () => {
    setFormData({
      matricula: '',
      nombre: '',
      apellido_paterno: '',
      apellido_materno: '',
      grado: '',
      grupo: '',
      turno: '',
      domicilio: '',
      telefono_contacto: '',
      telefono_emergencia: '',
      curp: '',
      nombre_tutor: '',
      telefono_tutor: '',
      ciclo_escolar_id: ciclos.find(c => c.activo)?.id || ''
    });
    setSelectedAlumno(null);
    setEditMode(false);
  };

  const getEstatusColor = (estatus) => {
    const colors = {
      activo: 'bg-green-100 text-green-800',
      baja_temporal: 'bg-amber-100 text-amber-800',
      baja_definitiva: 'bg-red-100 text-red-800',
      traslado: 'bg-blue-100 text-blue-800',
      egresado: 'bg-purple-100 text-purple-800'
    };
    return colors[estatus] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="alumnos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">Alumnos</h1>
          <p className="text-muted-foreground">Gestión de datos de alumnos</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters"
          >
            <Funnel size={16} className="mr-1" />
            Filtros
          </Button>
          
          {hasPermission(['administrador']) && (
            <>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleDownloadTemplate}
                data-testid="download-template"
              >
                <Download size={16} className="mr-1" />
                Plantilla
              </Button>
              
              <label>
                <Button variant="outline" size="sm" asChild>
                  <span>
                    <Upload size={16} className="mr-1" />
                    Cargar Excel
                  </span>
                </Button>
                <input 
                  type="file" 
                  accept=".xlsx,.xls" 
                  className="hidden" 
                  onChange={handleUpload}
                  data-testid="upload-input"
                />
              </label>
            </>
          )}
          
          {hasPermission(['administrador', 'direccion']) && (
            <Button 
              size="sm"
              onClick={() => { resetForm(); setShowModal(true); }}
              data-testid="add-alumno"
            >
              <Plus size={16} className="mr-1" />
              Nuevo Alumno
            </Button>
          )}
        </div>
      </div>

      {/* Upload Result */}
      {uploadResult && (
        <Card className="border-primary/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Resultado de carga masiva</p>
                <p className="text-sm text-muted-foreground">
                  Creados: {uploadResult.created} | Errores: {uploadResult.errors?.length || 0}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setUploadResult(null)}>
                <X size={16} />
              </Button>
            </div>
            {uploadResult.errors?.length > 0 && (
              <ScrollArea className="h-32 mt-2">
                <ul className="text-sm text-destructive space-y-1">
                  {uploadResult.errors.map((err) => (
                    <li key={err}>{err}</li>
                  ))}
                </ul>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      {showFilters && (
        <Card data-testid="filters-panel">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              <div>
                <Label className="text-xs">Matrícula</Label>
                <Input
                  placeholder="Buscar..."
                  value={filters.matricula}
                  onChange={(e) => handleFilterChange('matricula', e.target.value)}
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs">Nombre</Label>
                <Input
                  placeholder="Buscar..."
                  value={filters.nombre}
                  onChange={(e) => handleFilterChange('nombre', e.target.value)}
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs">Grado</Label>
                <Select value={filters.grado} onValueChange={(v) => handleFilterChange('grado', v)}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="1">1°</SelectItem>
                    <SelectItem value="2">2°</SelectItem>
                    <SelectItem value="3">3°</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Grupo</Label>
                <Select value={filters.grupo} onValueChange={(v) => handleFilterChange('grupo', v)}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="A">A</SelectItem>
                    <SelectItem value="B">B</SelectItem>
                    <SelectItem value="C">C</SelectItem>
                    <SelectItem value="D">D</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Turno</Label>
                <Select value={filters.turno} onValueChange={(v) => handleFilterChange('turno', v)}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="matutino">Matutino</SelectItem>
                    <SelectItem value="vespertino">Vespertino</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">PATRE</Label>
                <Select value={filters.en_patre} onValueChange={(v) => handleFilterChange('en_patre', v)}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="true">En PATRE</SelectItem>
                    <SelectItem value="false">Sin PATRE</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button variant="ghost" size="sm" onClick={clearFilters} className="w-full">
                  Limpiar
                </Button>
              </div>
            </div>

            {hasPermission(['administrador', 'direccion']) && (
              <div className="flex items-center gap-2 pt-3 border-t mt-3">
                <input
                  id="incluir-egresados"
                  type="checkbox"
                  className="h-4 w-4 rounded border-input"
                  checked={filters.incluir_egresados}
                  onChange={(e) => handleFilterChange('incluir_egresados', e.target.checked)}
                  data-testid="toggle-egresados-alumnos"
                />
                <Label htmlFor="incluir-egresados" className="cursor-pointer text-sm">
                  Mostrar alumnos <strong>egresados</strong> (archivados)
                </Label>
                {filters.incluir_egresados && (
                  <Badge variant="secondary" className="ml-2 text-xs">Vista de archivo activa</Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="py-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-medium">
              {total} alumnos encontrados
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="w-full">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[100px]">Matrícula</TableHead>
                  <TableHead>Nombre Completo</TableHead>
                  <TableHead className="w-[80px]">Grado</TableHead>
                  <TableHead className="w-[80px]">Grupo</TableHead>
                  <TableHead className="w-[100px]">Turno</TableHead>
                  <TableHead className="w-[100px]">Estatus</TableHead>
                  <TableHead className="w-[80px]">PATRE</TableHead>
                  <TableHead className="w-[100px] text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="spinner mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : alumnos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      <Student size={40} className="mx-auto mb-2 opacity-50" />
                      No se encontraron alumnos
                    </TableCell>
                  </TableRow>
                ) : (
                  alumnos.map((alumno) => (
                    <TableRow key={alumno.id} className="hover:bg-muted/30">
                      <TableCell className="font-mono text-sm">{alumno.matricula}</TableCell>
                      <TableCell className="font-medium min-w-[160px] max-w-[260px]">
                        <span
                          className="text-xs sm:text-sm md:text-base break-words whitespace-normal leading-tight block"
                          title={formatNombreCompleto(alumno)}
                        >
                          {formatNombreCompleto(alumno)}
                        </span>
                      </TableCell>
                      <TableCell>{alumno.grado}°</TableCell>
                      <TableCell>{alumno.grupo}</TableCell>
                      <TableCell className="capitalize">{alumno.turno}</TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${getEstatusColor(alumno.estatus)}`}>
                          {alumno.estatus?.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {alumno.en_patre && (
                          <Warning size={18} className="text-amber-500" weight="fill" />
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openDetailModal(alumno)}
                            data-testid={`view-${alumno.matricula}`}
                          >
                            <Eye size={16} />
                          </Button>
                          {hasPermission(['administrador', 'direccion']) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => openEditModal(alumno)}
                              data-testid={`edit-${alumno.matricula}`}
                            >
                              <PencilSimple size={16} />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={() => setDeleteTarget(alumno)}
                              title="Eliminar alumno (cascade: inscripciones e incidencias)"
                              data-testid={`delete-${alumno.matricula}`}
                            >
                              <Trash size={16} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
          
          {/* Pagination */}
          {total > 50 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-muted-foreground">
                Página {page} de {Math.ceil(total / 50)}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= Math.ceil(total / 50)}
                  onClick={() => setPage(p => p + 1)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editMode ? 'Editar Alumno' : 'Nuevo Alumno'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Matrícula *</Label>
                <Input
                  value={formData.matricula}
                  onChange={(e) => setFormData(p => ({ ...p, matricula: e.target.value }))}
                  required
                  disabled={editMode}
                  data-testid="form-matricula"
                />
              </div>
              <div>
                <Label>CURP</Label>
                <Input
                  value={formData.curp}
                  onChange={(e) => setFormData(p => ({ ...p, curp: e.target.value }))}
                  data-testid="form-curp"
                />
              </div>
              <div>
                <Label>Nombre(s) *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData(p => ({ ...p, nombre: e.target.value }))}
                  required
                  data-testid="form-nombre"
                />
              </div>
              <div>
                <Label>Apellido Paterno *</Label>
                <Input
                  value={formData.apellido_paterno}
                  onChange={(e) => setFormData(p => ({ ...p, apellido_paterno: e.target.value }))}
                  required
                  data-testid="form-apellido-paterno"
                />
              </div>
              <div>
                <Label>Apellido Materno</Label>
                <Input
                  value={formData.apellido_materno}
                  onChange={(e) => setFormData(p => ({ ...p, apellido_materno: e.target.value }))}
                  data-testid="form-apellido-materno"
                />
              </div>
              <div>
                <Label>Grado *</Label>
                <Select 
                  value={formData.grado} 
                  onValueChange={(v) => setFormData(p => ({ ...p, grado: v }))}
                >
                  <SelectTrigger data-testid="form-grado">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1°</SelectItem>
                    <SelectItem value="2">2°</SelectItem>
                    <SelectItem value="3">3°</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Grupo *</Label>
                <Select 
                  value={formData.grupo} 
                  onValueChange={(v) => setFormData(p => ({ ...p, grupo: v }))}
                >
                  <SelectTrigger data-testid="form-grupo">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A">A</SelectItem>
                    <SelectItem value="B">B</SelectItem>
                    <SelectItem value="C">C</SelectItem>
                    <SelectItem value="D">D</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Turno *</Label>
                <Select 
                  value={formData.turno} 
                  onValueChange={(v) => setFormData(p => ({ ...p, turno: v }))}
                >
                  <SelectTrigger data-testid="form-turno">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="matutino">Matutino</SelectItem>
                    <SelectItem value="vespertino">Vespertino</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Domicilio</Label>
                <Input
                  value={formData.domicilio}
                  onChange={(e) => setFormData(p => ({ ...p, domicilio: e.target.value }))}
                  data-testid="form-domicilio"
                />
              </div>
              <div>
                <Label>Teléfono de Contacto</Label>
                <Input
                  value={formData.telefono_contacto}
                  onChange={(e) => setFormData(p => ({ ...p, telefono_contacto: e.target.value }))}
                  data-testid="form-telefono"
                />
              </div>
              <div>
                <Label>Teléfono de Emergencia</Label>
                <Input
                  value={formData.telefono_emergencia}
                  onChange={(e) => setFormData(p => ({ ...p, telefono_emergencia: e.target.value }))}
                />
              </div>
              <div>
                <Label>Nombre del Tutor</Label>
                <Input
                  value={formData.nombre_tutor}
                  onChange={(e) => setFormData(p => ({ ...p, nombre_tutor: e.target.value }))}
                  data-testid="form-tutor"
                />
              </div>
              <div>
                <Label>Teléfono del Tutor</Label>
                <Input
                  value={formData.telefono_tutor}
                  onChange={(e) => setFormData(p => ({ ...p, telefono_tutor: e.target.value }))}
                />
              </div>
              
              {editMode && (
                <>
                  <div>
                    <Label>Estatus</Label>
                    <Select 
                      value={formData.estatus} 
                      onValueChange={(v) => setFormData(p => ({ ...p, estatus: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="activo">Activo</SelectItem>
                        <SelectItem value="baja_temporal">Baja Temporal</SelectItem>
                        <SelectItem value="baja_definitiva">Baja Definitiva</SelectItem>
                        <SelectItem value="traslado">Traslado</SelectItem>
                        <SelectItem value="egresado">Egresado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="submit-alumno">
                {editMode ? 'Guardar Cambios' : 'Crear Alumno'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Detalle del Alumno</DialogTitle>
          </DialogHeader>
          {selectedAlumno && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Student size={32} className="text-primary" />
                </div>
                <div>
                  <p className="font-heading font-bold text-base sm:text-lg md:text-xl break-words leading-tight">
                    {formatNombreCompleto(selectedAlumno)}
                  </p>
                  <p className="text-muted-foreground">Matrícula: {selectedAlumno.matricula}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Grado y Grupo</p>
                  <p className="font-medium">{selectedAlumno.grado}° {selectedAlumno.grupo}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Turno</p>
                  <p className="font-medium capitalize">{selectedAlumno.turno}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Estatus</p>
                  <Badge className={getEstatusColor(selectedAlumno.estatus)}>
                    {selectedAlumno.estatus?.replace(/_/g, ' ')}
                  </Badge>
                </div>
                <div>
                  <p className="text-muted-foreground">CURP</p>
                  <p className="font-medium font-mono text-xs">{selectedAlumno.curp || '-'}</p>
                </div>
                {user?.rol !== 'docente' && (
                  <>
                    <div className="col-span-2">
                      <p className="text-muted-foreground">Domicilio</p>
                      <p className="font-medium">{selectedAlumno.domicilio || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Teléfono</p>
                      <p className="font-medium">{selectedAlumno.telefono_contacto || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Tel. Emergencia</p>
                      <p className="font-medium">{selectedAlumno.telefono_emergencia || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Tutor</p>
                      <p className="font-medium">{selectedAlumno.nombre_tutor || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Tel. Tutor</p>
                      <p className="font-medium">{selectedAlumno.telefono_tutor || '-'}</p>
                    </div>
                  </>
                )}
              </div>
              
              {selectedAlumno.en_patre && (
                <Card className="bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Warning size={18} className="text-amber-600" weight="fill" />
                      <span className="font-medium text-amber-800 dark:text-amber-200">En Programa PATRE</span>
                    </div>
                    {selectedAlumno.motivo_patre && (
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        <strong>Motivo:</strong> {selectedAlumno.motivo_patre}
                      </p>
                    )}
                    {selectedAlumno.materias_riesgo?.length > 0 && (
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        <strong>Materias en riesgo:</strong> {selectedAlumno.materias_riesgo.join(', ')}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}
        title="Eliminar alumno (cascade)"
        description="Se eliminarán en cascada las inscripciones e incidencias vinculadas a este alumno (soft-delete). Esta acción es irreversible desde la UI."
        itemLabel={deleteTarget ? `${formatNombreCompleto(deleteTarget)} — ${deleteTarget.matricula} (${deleteTarget.grado}° ${deleteTarget.grupo})` : ''}
        onConfirm={async (motivo) => {
          setDeleting(true);
          try {
            const resp = await deleteAlumno(deleteTarget.id, motivo);
            const c = resp?.data?.cascade || {};
            toast.success(`Alumno eliminado. Cascade: ${c.inscripciones || 0} inscripción(es), ${c.expedientes || 0} incidencia(s)`);
            setDeleteTarget(null);
            fetchAlumnos();
          } catch (error) {
            toast.error(error.response?.data?.detail || 'No se pudo eliminar el alumno');
          } finally {
            setDeleting(false);
          }
        }}
        loading={deleting}
      />
    </div>
  );
};

export default AlumnosPage;
